import React, { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';

const TABS = {
    NEW: 'new',
    EXISTING: 'existing',
};

// Lets the user drop their current campaign selection into either a brand
// new group or an existing one — the two are different enough inputs
// (a name vs. a picker) that tabs read clearer than one combined form.
const GroupSelectionDialog = React.memo(function GroupSelectionDialog({
    open,
    onClose,
    selectedCount,
    existingGroups,
    onCreateNew,
    onAddToExisting,
}) {
    const [activeTab, setActiveTab] = useState(TABS.NEW);
    const [name, setName] = useState('');
    const [existingGroup, setExistingGroup] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleClose = () => {
        if (submitting) return;
        setName('');
        setExistingGroup(null);
        setActiveTab(TABS.NEW);
        onClose();
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            if (activeTab === TABS.NEW) {
                await onCreateNew(name.trim());
            } else if (existingGroup) {
                await onAddToExisting(existingGroup._id);
            }
            handleClose();
        } catch (error) {
            // onCreateNew/onAddToExisting already surface the failure (a Swal
            // alert) before rethrowing — this just keeps the dialog open with
            // the user's input intact so they can retry, instead of letting
            // the rejection bubble up as an unhandled-promise-rejection.
        } finally {
            setSubmitting(false);
        }
    };

    const canConfirm =
        activeTab === TABS.NEW ? name.trim().length > 0 : Boolean(existingGroup);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Group {selectedCount} campaign{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogContent>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                    <Tab label="New group" value={TABS.NEW} />
                    <Tab label="Existing group" value={TABS.EXISTING} disabled={!existingGroups.length} />
                </Tabs>

                {activeTab === TABS.NEW ? (
                    <TextField
                        autoFocus
                        fullWidth
                        label="Group name"
                        placeholder="e.g. Diwali 2026 Bundle"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                ) : existingGroups.length ? (
                    <Autocomplete
                        options={existingGroups}
                        getOptionLabel={(g) => g.name}
                        value={existingGroup}
                        onChange={(e, value) => setExistingGroup(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Choose a group" autoFocus />
                        )}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No groups yet — create one first.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} color="secondary">
                    Cancel
                </Button>
                <Box sx={{ position: 'relative' }}>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={!canConfirm || submitting}
                    >
                        {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
});

export default GroupSelectionDialog;
